import { Stack, Text, SegmentedControl, Switch, Chip, Group } from '@mantine/core';

type CookingPreferenceProps = {
  value: {
    cookingSkill: 'beginner' | 'intermediate' | 'advanced';
    includeLeftovers: boolean;
    preferredCookingMethods?: string[];
  };
  onChange: (value: {
    cookingSkill: 'beginner' | 'intermediate' | 'advanced';
    includeLeftovers: boolean;
    preferredCookingMethods?: string[];
  }) => void;
};

export const CookingPreference = ({ value, onChange }: CookingPreferenceProps) => {
  const preferredCookingMethods = value.preferredCookingMethods || [];

  return (
    <Stack gap="xl">
      {/* Cooking Skill */}
      <Stack gap="md">
        <Text fw={500}>Cooking skill level</Text>
        
        {/* Mobile: Vertical */}
        <SegmentedControl
          value={value.cookingSkill}
          onChange={(val) => onChange({ ...value, cookingSkill: val as 'beginner' | 'intermediate' | 'advanced' })}
          data={[
            { 
              label: 'Beginner', 
              value: 'beginner',
            },
            { 
              label: 'Intermediate', 
              value: 'intermediate',
            },
            { 
              label: 'Advanced', 
              value: 'advanced',
            },
          ]}
          orientation="vertical"
          size="md"
          color="orange"
          fullWidth
          hiddenFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />
        
        {/* Desktop: Horizontal */}
        <SegmentedControl
          value={value.cookingSkill}
          onChange={(val) => onChange({ ...value, cookingSkill: val as 'beginner' | 'intermediate' | 'advanced' })}
          data={[
            { 
              label: 'Beginner', 
              value: 'beginner',
            },
            { 
              label: 'Intermediate', 
              value: 'intermediate',
            },
            { 
              label: 'Advanced', 
              value: 'advanced',
            },
          ]}
          orientation="horizontal"
          size="md"
          color="orange"
          visibleFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />
      </Stack>

      {/* Leftovers Preference */}
      <Stack gap="md">
        <Text fw={500}>Leftovers preference</Text>

        <Switch
          checked={value.includeLeftovers}
          onChange={(event) => onChange({ ...value, includeLeftovers: event.currentTarget.checked })}
          label="Include leftovers"
          size="md"
          color="orange"
          styles={{
            root: {
              alignSelf: 'flex-start'
            }
          }}
        />
      </Stack>

      {/* Cooking Style Preferences */}
      <Stack gap="md">
        <Text fw={500}>Cooking style preferences</Text>
        <Text size="sm" c="dimmed">How do you prefer to cook? (Select all that apply)</Text>

        <Chip.Group
          multiple
          value={preferredCookingMethods}
          onChange={(values) => onChange({ ...value, preferredCookingMethods: values })}
        >
          {/* chip-cloud: shrinks the pills below 375px so long labels don't
              overflow the accordion panel (see index.css) */}
          <Group gap="xs" className="chip-cloud">
            <Chip value="one_pot" color="orange" variant="filled">One-pot / Sheet pan meals</Chip>
            <Chip value="set_forget" color="orange" variant="filled">Set-and-forget (slow cooker)</Chip>
            <Chip value="stovetop" color="orange" variant="filled">Stovetop cooking</Chip>
            <Chip value="grilling" color="orange" variant="filled">Grilling / BBQ</Chip>
            <Chip value="baking" color="orange" variant="filled">Baking / Roasting</Chip>
            <Chip value="no_cook" color="orange" variant="filled">No-cook / Minimal prep</Chip>
          </Group>
        </Chip.Group>
      </Stack>
    </Stack>
  );
};
